import {
  ApplicationRef, Component, ElementRef, EventEmitter,
  HostListener, Injector, Input, OnDestroy, OnInit, Output,
  TemplateRef, ViewChild, ViewContainerRef, computed, signal
} from '@angular/core';
import { AbstractControl, ControlValueAccessor, NG_VALIDATORS, NG_VALUE_ACCESSOR, NgControl, ValidationErrors } from '@angular/forms';
import { NgClass, NgStyle, NgTemplateOutlet } from '@angular/common';
import { DomPortalOutlet, TemplatePortal } from '@angular/cdk/portal';
import { PhoneNumberUtil } from 'google-libphonenumber';
import { ICountryCodes, countryCodes } from './helpers/countries-helper';
import { STRING_UPPER_LOWER_SPECIAL_CHARACTERS_REGEX } from './helpers/regex-helper';
import { GeolocationService } from '../../../services/geolocation.service';

export interface PhoneNumberValue {
  countryCode: string;
  contactNumber: string;
  countryShortName: string;
}

@Component({
  selector: 'app-phone-input',
  standalone: true,
  imports: [NgClass, NgStyle, NgTemplateOutlet],
  templateUrl: './phone-input.component.html',
  styleUrl: './phone-input.component.scss',
  providers: [
    { provide: NG_VALUE_ACCESSOR, useExisting: PhoneInputComponent, multi: true },
    { provide: NG_VALIDATORS, useExisting: PhoneInputComponent, multi: true },
  ],
})
export class PhoneInputComponent implements ControlValueAccessor, OnInit, OnDestroy {
  @Input() readOnly = false;
  @Input() appendTo = '';
  @Output() phoneNumberChange = new EventEmitter<PhoneNumberValue>();
  @ViewChild('dropdownTemplate') dropdownTemplate!: TemplateRef<unknown>;

  readonly dropDownOptions = signal<ICountryCodes[]>(countryCodes);
  readonly searchDropDownOptions = signal<ICountryCodes[]>(countryCodes);
  readonly searchText = signal('');
  readonly isFocused = signal(false);
  readonly isDropDownOpen = signal(false);
  readonly selectedPhoneNumber = signal<PhoneNumberValue>({ countryCode: '1', contactNumber: '', countryShortName: 'US' });
  readonly dropdownStyles = signal<Record<string, string>>({});

  readonly countryFlag = computed(() => this.flagEmoji(this.selectedPhoneNumber().countryShortName));
  readonly hasError = computed(() => !!(
    (this.formControl?.dirty || this.formControl?.touched) &&
    this.formControl?.invalid &&
    !this.isFocused()
  ));

  formControl: NgControl | null = null;

  // eslint-disable-next-line @typescript-eslint/no-empty-function, @typescript-eslint/no-unused-vars
  onChange = (_value: PhoneNumberValue) => { };
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  onTouch = () => { };

  private readonly phoneNumberUtil = PhoneNumberUtil.getInstance();
  private portalOutlet: DomPortalOutlet | null = null;
  private portalHostElement: HTMLElement | null = null;
  private portalPositionListener: (() => void) | null = null;

  constructor(
    private readonly ref: ElementRef,
    private readonly injector: Injector,
    private readonly geolocationService: GeolocationService,
    private readonly appRef: ApplicationRef,
    private readonly viewContainerRef: ViewContainerRef,
  ) { }

  ngOnInit(): void {
    this.geolocationService.fetchCountryCodes();
    try {
      this.formControl = this.injector.get(NgControl);
    } catch (err) {
      console.error(err);
    }
  }

  ngOnDestroy(): void {
    this.closePortal();
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: Event): void {
    const target = event.target as Node;
    if (!this.ref.nativeElement.contains(target) && !this.portalHostElement?.contains(target)) {
      if (this.isDropDownOpen()) {
        this.isDropDownOpen.set(false);
        this.resetSearch();
        this.closePortal();
      }
    }
  }

  toggleDropDown(): void {
    const opening = !this.isDropDownOpen();
    this.isDropDownOpen.set(opening);
    this.onTouch();

    if (opening) {
      if (this.appendTo) this.openPortal();
    } else {
      this.resetSearch();
      if (this.appendTo) this.closePortal();
    }
  }

  setCountryCode(country: ICountryCodes): void {
    this.selectedPhoneNumber.update(p => ({
      ...p,
      countryCode: country.phoneCode.replace('+', ''),
      countryShortName: country.code,
    }));
    this.emitChange();
    this.toggleDropDown();
  }

  setPhoneNumber(event: Event): void {
    const input = event.target as HTMLInputElement;
    input.value = input.value.replace(STRING_UPPER_LOWER_SPECIAL_CHARACTERS_REGEX, '');
    this.selectedPhoneNumber.update(p => ({ ...p, contactNumber: input.value }));
    this.emitChange();
  }

  searchCountry(event: Event) {
    const term = (event.target as HTMLInputElement).value;
    this.searchText.set(term);
    const lower = term.toLowerCase();
    this.searchDropDownOptions.set(
      this.dropDownOptions().filter(c =>
        c.name.toLowerCase().includes(lower) ||
        c.phoneCode.toLowerCase().includes(lower) ||
        c.code.toLowerCase().includes(lower)
      )
    );
  }

  flagEmoji(code: string): string {
    if (!code || code.length !== 2) return '🌐';
    return [...code.toUpperCase()].map(c => String.fromCodePoint(c.charCodeAt(0) + 127397)).join('');
  }

  writeValue(value: PhoneNumberValue | string | null): void {
    if (!value) {
      this.selectedPhoneNumber.set(this.geolocationService.getCountry());
      return;
    }
    if (typeof value === 'string') {
      this.selectedPhoneNumber.set(this.parsePhoneNumber(value));
    } else {
      const fallback = this.geolocationService.getCountry();
      this.selectedPhoneNumber.set({
        countryCode: value.countryCode || fallback.countryCode,
        contactNumber: value.contactNumber || fallback.contactNumber,
        countryShortName: value.countryShortName || fallback.countryShortName,
      });
    }
  }

  registerOnChange(fn: (value: PhoneNumberValue) => void): void { this.onChange = fn; }
  registerOnTouched(fn: () => void): void { this.onTouch = fn; }

  validate(control: AbstractControl): ValidationErrors | null {
    const v = control.value as PhoneNumberValue;
    if (!v?.countryCode?.trim() || !v?.contactNumber?.trim()) return null;
    try {
      const parsed = this.phoneNumberUtil.parseAndKeepRawInput(
        `+${v.countryCode.trim()}${v.contactNumber.trim()}`,
        v.countryShortName,
      );
      return this.phoneNumberUtil.isValidNumber(parsed) ? null : { invalidNumber: true };
    } catch {
      return { invalidNumber: true };
    }
  }

  private emitChange(): void {
    this.onChange(this.selectedPhoneNumber());
    this.onTouch();
    this.phoneNumberChange.emit(this.selectedPhoneNumber());
  }

  private resetSearch(): void {
    this.searchText.set('');
    this.searchDropDownOptions.set(this.dropDownOptions());
  }

  private parsePhoneNumber(fullNumber: string): PhoneNumberValue {
    try {
      const parsed = this.phoneNumberUtil.parse(`+${fullNumber}`);
      return {
        countryCode: `${parsed.getCountryCode()}`,
        contactNumber: parsed.getNationalNumber()?.toString() ?? '',
        countryShortName: this.phoneNumberUtil.getRegionCodeForNumber(parsed) ?? '',
      };
    } catch {
      return this.geolocationService.getCountry();
    }
  }

  private updatePortalPosition(): void {
    const rect = (this.ref.nativeElement as HTMLElement).getBoundingClientRect();
    this.dropdownStyles.set({
      position: 'fixed',
      top: `${rect.bottom + 4}px`,
      left: `${rect.left}px`,
      width: `${rect.width}px`,
      'z-index': '9999',
    });
  }

  private openPortal(): void {
    this.updatePortalPosition();

    this.portalHostElement = document.createElement('div');
    document.body.appendChild(this.portalHostElement);
    this.portalOutlet = new DomPortalOutlet(this.portalHostElement, null as any, this.appRef, this.injector);
    this.portalOutlet.attach(new TemplatePortal(this.dropdownTemplate, this.viewContainerRef));

    this.portalPositionListener = () => this.updatePortalPosition();
    window.addEventListener('scroll', this.portalPositionListener, true);
    window.addEventListener('resize', this.portalPositionListener);
  }

  private closePortal(): void {
    if (this.portalPositionListener) {
      window.removeEventListener('scroll', this.portalPositionListener, true);
      window.removeEventListener('resize', this.portalPositionListener);
      this.portalPositionListener = null;
    }
    this.portalOutlet?.dispose();
    this.portalOutlet = null;
    this.portalHostElement?.remove();
    this.portalHostElement = null;
  }
}
